import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/Dialog';

interface CrmConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnect: (crmOrgUrl: string) => void;
  isConnecting: boolean;
}

export const CrmConnectDialog = ({
  open,
  onOpenChange,
  onConnect,
  isConnecting,
}: CrmConnectDialogProps) => {
  const { t } = useTranslation('goals');
  const [orgName, setOrgName] = useState('');

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedName = orgName.trim();
    if (!trimmedName) return;

    onConnect(`${trimmedName}.dynamics.com`);
    setOrgName('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('integrationsDialog.crmConnectTitle')}</DialogTitle>
          <DialogDescription>
            {t('integrationsDialog.crmConnectDescription')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="py-4">
            <div className="flex items-center gap-0 rounded-md border border-input">
              <span className="shrink-0 bg-muted px-3 py-2 text-sm text-muted-foreground">
                {t('integrationsDialog.crmUrlPrefix')}
              </span>
              <Input
                placeholder={t('integrationsDialog.crmUrlPlaceholder')}
                value={orgName}
                onChange={(event) => setOrgName(event.target.value)}
                autoFocus
                className="rounded-none border-0 focus-visible:ring-0"
              />
              <span className="shrink-0 bg-muted px-3 py-2 text-sm text-muted-foreground">
                {t('integrationsDialog.crmUrlSuffix')}
              </span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {t('integrationsDialog.crmUrlHelper')}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('integrationsDialog.cancel')}
            </Button>
            <Button type="submit" disabled={!orgName.trim() || isConnecting}>
              {t('integrationsDialog.continue')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
